import { SITE, has, ratesValid } from "@/config/site";
import { amortize, aprIncludingFee, fmtNGNWhole } from "@/lib/finance";

const PRINCIPAL = 150000;
const MONTHS = 6;

/**
 * Representative-example strip. Appears directly above the footer on every page
 * (and below the hero on the homepage) WHEN LENDING_ENABLED is true.
 *
 * Refuses to render — and logs a console warning — if RATE_REPRESENTATIVE_PA
 * is outside the [RATE_MIN_PA, RATE_MAX_PA] band, so inconsistent config can
 * never be shown to parents.
 */
export default function RepExample() {
  if (!SITE.LENDING_ENABLED) return null;

  const { RATE_MIN_PA: lo, RATE_MAX_PA: hi, RATE_REPRESENTATIVE_PA: rep, APPLICATION_FEE_PCT: feePct } = SITE;

  if (!ratesValid()) return null;

  const { monthly, total: interestTotal, interest } = amortize(PRINCIPAL, rep, MONTHS);
  const procFee = has(feePct) ? (PRINCIPAL * feePct / 100) : 0;
  const grandTotal = interestTotal + procFee;
  const costOfLoan = interest + procFee;
  const aprWithFee = has(feePct) ? aprIncludingFee(PRINCIPAL, rep, MONTHS, feePct) : rep;

  return (
    <section
      className="bg-[#F0EFEA] border-t border-[#E5E3DB]"
      aria-label="Representative example"
      data-testid="rep-example-strip"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-16 py-6 text-xs sm:text-sm text-[#4F5D4F] leading-relaxed">
        <span className="font-semibold uppercase tracking-[0.14em] text-[#0A3622] block mb-1">
          Representative example
        </span>
        Spread a {fmtNGNWhole(PRINCIPAL)} school fee over {MONTHS} months at{" "}
        <span className="font-semibold text-[#132A13]">{rep}%</span> a year.
        You pay <span className="font-semibold text-[#132A13]">{fmtNGNWhole(monthly)}</span> a month and{" "}
        <span className="font-semibold text-[#132A13]">{fmtNGNWhole(grandTotal)}</span> in total
        (including a one-time processing fee of <span className="font-semibold text-[#132A13]">{feePct}%</span>,{" "}
        {fmtNGNWhole(procFee)}, added to your loan). The cost of the loan is{" "}
        <span className="font-semibold text-[#132A13]">{fmtNGNWhole(costOfLoan)}</span>.
        APR including the processing fee is{" "}
        <span className="font-semibold text-[#132A13]">{aprWithFee}%</span>.
        {" "}Rates from <span className="font-semibold text-[#132A13]">{lo}%</span> to{" "}
        <span className="font-semibold text-[#132A13]">{hi}%</span> a year depending on your assessment.
        Loans subject to approval. Borrow only what you can repay.
      </div>
    </section>
  );
}
